import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Menu, Bell, Search, LogOut, Plus } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { getUserRoles, hasRole } from '../utils/roleUtils';
import { classNames } from '../utils/helpers';
import Avatar from './ui/Avatar';
import api from '../services/api';
import { useToast } from './Toast';

function useOutsideClose<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return ref;
}

export default function TopBar({ onOpenSidebar }: { onOpenSidebar: () => void }) {
  const { user, logout } = useAuth();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();

  const [bellOpen, setBellOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const bellRef = useOutsideClose<HTMLDivElement>(() => setBellOpen(false));
  const menuRef = useOutsideClose<HTMLDivElement>(() => setMenuOpen(false));

  const [isAvailable, setIsAvailable] = useState(true);
  const [savingAvailability, setSavingAvailability] = useState(false);
  useEffect(() => { setIsAvailable(user?.isAvailable ?? true); }, [user?.isAvailable]);

  const showAvailabilityToggle = hasRole(user, 'it_manager') || hasRole(user, 'admin') || hasRole(user, 'it_staff');

  const toggleAvailability = async () => {
    const next = !isAvailable;
    setSavingAvailability(true);
    try {
      await api.put('/users/me/availability', { isAvailable: next });
      setIsAvailable(next);
      toast(next ? 'You are marked as Available' : 'You are marked as Not Available — your open tickets will be automatically reassigned', 'success');
    } catch {
      toast('Failed to update availability', 'error');
    } finally { setSavingAvailability(false); }
  };

  if (!user) return null;
  const roleLabel = getUserRoles(user).map(r => r.replace(/_/g, ' ')).join(' · ');

  const handleLogout = async () => {
    await logout();
    instance.clearCache();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 h-14 px-4 bg-white border-b border-gray-200">
      <button onClick={onOpenSidebar} aria-label="Open menu" className="md:hidden text-gray-500 hover:text-navy">
        <Menu size={20} />
      </button>

      {/* Search (placeholder — wires to global search later) */}
      <div className="relative flex-1 max-w-md hidden sm:block">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          placeholder="Search tickets…"
          onKeyDown={e => { if (e.key === 'Enter') { const v = (e.target as HTMLInputElement).value.trim(); if (v) navigate(`/tickets?search=${encodeURIComponent(v)}`); } }}
          className="w-full text-sm rounded-lg bg-gray-100 border border-transparent pl-9 pr-3 py-2 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none"
        />
      </div>

      <div className="flex items-center gap-1.5 ml-auto">
        <button onClick={() => navigate('/tickets/new')}
          className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-brand-fg rounded-lg px-3 py-2"
          style={{ background: 'var(--brand-primary, #5B21FF)' }}>
          <Plus size={16} /> New Ticket
        </button>

        {/* Notifications */}
        <div className="relative" ref={bellRef}>
          <button onClick={() => setBellOpen(o => !o)} aria-label="Notifications"
            className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-navy">
            <Bell size={19} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          {bellOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-dropdown border border-gray-100 animate-scale-in overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <span className="text-sm font-semibold text-navy">Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-brand hover:underline">Mark all read</button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-gray-400">No notifications</p>
                ) : notifications.slice(0, 12).map(n => (
                  <button
                    key={n.id}
                    onClick={() => { markRead(n.id); setBellOpen(false); if ((n as any).ticketId) navigate(`/tickets/${(n as any).ticketId}`); }}
                    className={classNames('w-full text-left px-4 py-2.5 border-b border-gray-50 hover:bg-gray-50',
                      !n.isRead && 'bg-brand/[0.04]')}
                  >
                    <div className="flex items-start gap-2">
                      {!n.isRead && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand shrink-0" />}
                      <span className="text-sm text-navy line-clamp-2">{(n as any).subject ?? 'Notification'}</span>
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={() => { setBellOpen(false); navigate('/notifications'); }}
                className="w-full text-center px-4 py-2.5 text-xs font-medium text-brand hover:bg-gray-50 border-t border-gray-100">
                View all notifications
              </button>
            </div>
          )}
        </div>

        {/* Profile menu */}
        <div className="relative" ref={menuRef}>
          <button onClick={() => setMenuOpen(o => !o)} className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100">
            <Avatar name={user.name} size="sm" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-dropdown border border-gray-100 animate-scale-in overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-navy truncate">{user.name}</p>
                <p className="text-xs text-gray-400 capitalize truncate">{roleLabel}</p>
              </div>
              {showAvailabilityToggle && (
                <button
                  onClick={toggleAvailability}
                  disabled={savingAvailability}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 border-b border-gray-100 disabled:opacity-50"
                >
                  <span className="flex items-center gap-2">
                    <span className={classNames('w-1.5 h-1.5 rounded-full', isAvailable ? 'bg-emerald-500' : 'bg-gray-400')} />
                    {isAvailable ? 'Available' : 'Not Available'}
                  </span>
                  <span className="text-xs text-brand">{isAvailable ? 'Mark as away' : 'Mark as back'}</span>
                </button>
              )}
              <button onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-red-600">
                <LogOut size={16} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
